import React, { useLayoutEffect, useState } from 'react';
import { Button } from '../UI';
import {
  computeTooltipPosition,
  getTooltipWidth,
  ONBOARDING_FIELD_TOOLTIP_HEIGHT,
  type TooltipPosition,
} from '../../utils/onboardingTooltipPosition';
import { Z_INDEX_TOUR_TOOLTIP, Z_INDEX_TOUR_TOOLTIP_MODAL } from '../../constants/zIndex';
import type { OnboardingStepTarget } from '../../constants/onboardingStepTargets';

export interface FieldGuideTooltipProps {
  target: OnboardingStepTarget;
  targetIndex: number;
  targetCount: number;
  targetRect: DOMRect | null;
  canGoNext: boolean;
  onNext: () => void;
  onAdvanceToNextStep?: () => void;
  onClose: () => void;
  isClosing?: boolean;
  aboveModal?: boolean;
}

export const FieldGuideTooltip: React.FC<FieldGuideTooltipProps> = ({
  target,
  targetIndex,
  targetCount,
  targetRect,
  canGoNext,
  onNext,
  onAdvanceToNextStep,
  onClose,
  isClosing = false,
  aboveModal = false,
}) => {
  const [tooltipWidth, setTooltipWidth] = useState(() => getTooltipWidth());
  const [position, setPosition] = useState<TooltipPosition>(() =>
    computeTooltipPosition(targetRect, ONBOARDING_FIELD_TOOLTIP_HEIGHT, {
      placementPreference: aboveModal ? 'modal' : 'default',
    })
  );

  useLayoutEffect(() => {
    setTooltipWidth(getTooltipWidth());
    setPosition(
      computeTooltipPosition(targetRect, ONBOARDING_FIELD_TOOLTIP_HEIGHT, {
        placementPreference: aboveModal ? 'modal' : 'default',
      })
    );
  }, [targetRect, aboveModal]);
  return (
    <div
      role="dialog"
      aria-label="Guia de preenchimento"
      className="pointer-events-auto fixed w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900"
      style={{
        top: position.top,
        left: position.left,
        width: tooltipWidth,
        zIndex: aboveModal ? Z_INDEX_TOUR_TOOLTIP_MODAL : Z_INDEX_TOUR_TOOLTIP,
      }}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Campo {targetIndex + 1} de {targetCount}
        {target.groupLabel ? (
          <span className="normal-case tracking-normal text-slate-600 dark:text-slate-300">
            {' '}
            · {target.groupLabel}
          </span>
        ) : null}
      </p>
      <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
        {target.hint}
      </p>
      <div className="mt-5 flex flex-col gap-2">
        {canGoNext ? (
          <Button type="button" variant="primary" className="w-full" onClick={onNext}>
            Próximo
          </Button>
        ) : target.advanceToNextStepOnFinish && onAdvanceToNextStep ? (
          <Button type="button" variant="primary" className="w-full" onClick={onAdvanceToNextStep}>
            Próximo passo
          </Button>
        ) : target.requiresInteraction ? (
          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            Clique no elemento destacado para continuar.
          </p>
        ) : (
          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            Conclua a ação acima para avançar ao próximo passo.
          </p>
        )}
        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={onClose}
          loading={isClosing}
          disabled={isClosing}
        >
          Fechar tour
        </Button>
      </div>
    </div>
  );
};
